import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import WeekSelector from "@/components/WeekSelector";
import CategoryFilter from "@/components/CategoryFilter";
import PromoGrid from "@/components/PromoGrid";
import MenuSection from "@/components/MenuSection";
import LocationHeader from "@/components/LocationHeader";
import LocationAccessGate from "@/components/LocationAccessGate";
import SmartFridgeBanner from "@/components/SmartFridgeBanner";
import BasketModal from "@/components/BasketModal";
import CheckoutModal from "@/components/CheckoutModal";
import QuantityModal from "@/components/QuantityModal";
import { MAX_QTY } from "@/components/QuantityInput";
import { preloadAllImages } from "@/components/MenuCard";
import { getFingerprint } from "@/lib/fingerprint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LocationMenuPage() {
  const { slug } = useParams();
  const [locationInfo, setLocationInfo] = useState(null);
  const [accessCode, setAccessCode] = useState(() => localStorage.getItem(`munchd-loc-code:${slug}`));
  const [verifying, setVerifying] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [rotatingKeys, setRotatingKeys] = useState(new Set());
  const isProgrammaticScrollRef = useRef(false);

  // Basket state — only used when mode === "weekly_order"
  const isWeeklyOrder = locationInfo?.mode === "weekly_order";
  const [basket, setBasket] = useState([]);
  const [basketOpen, setBasketOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [quantityItem, setQuantityItem] = useState(null);
  const [quantityOpen, setQuantityOpen] = useState(false);

  // Fetch location info (public — no code needed)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/locations/${slug}/info`)
      .then((res) => {
        if (!cancelled) setLocationInfo(res.data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setVerifying(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Re-verify stored code on mount — if invalid, clear and re-prompt
  useEffect(() => {
    if (!accessCode || !locationInfo) return;
    axios
      .post(`${API}/locations/${slug}/verify`, { access_code: accessCode })
      .catch(() => {
        localStorage.removeItem(`munchd-loc-code:${slug}`);
        setAccessCode(null);
      });
  }, [accessCode, locationInfo, slug]);

  // Once we have a code + location info, load the menu
  const loadData = useCallback(async () => {
    if (!accessCode) return;
    setLoading(true);
    try {
      const headers = {
        "X-Location-Code": accessCode,
        "X-User-Fingerprint": getFingerprint(),
      };
      const [menuRes, catRes, promoRes] = await Promise.all([
        axios.get(`${API}/locations/${slug}/menu`, { headers }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/locations/${slug}/promotions`, { headers }),
      ]);

      const sortedCategories = catRes.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      const rotating = new Set(sortedCategories.filter((c) => c.is_rotating).map((c) => c.key));

      setMenuItems(menuRes.data);
      setCategories(sortedCategories);
      setPromotions(promoRes.data);
      setRotatingKeys(rotating);

      // Preload images for items at this location
      const allIds = menuRes.data.map((i) => i.id);
      if (allIds.length) preloadAllImages(allIds);
    } catch (err) {
      // If the code went stale, force re-prompt
      if (err.response?.status === 401) {
        localStorage.removeItem(`munchd-loc-code:${slug}`);
        setAccessCode(null);
      }
    } finally {
      setLoading(false);
    }
  }, [accessCode, slug]);

  useEffect(() => {
    if (accessCode && locationInfo) loadData();
  }, [accessCode, locationInfo, loadData]);

  // Week filter
  const filteredItems = useMemo(() => {
    if (!menuItems.length || !selectedWeek) return [];
    const weekNumber = selectedWeek.week_number;
    return menuItems.filter((item) => {
      const isRotating = rotatingKeys.has(item.category);
      if (isRotating) return item.week_number === weekNumber;
      return true;
    });
  }, [menuItems, selectedWeek, rotatingKeys]);

  const getCategoryItems = useCallback(
    (categoryKey) =>
      filteredItems
        .filter((item) => item.category === categoryKey)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [filteredItems]
  );

  // Only show categories that have at least one item available at this location
  const visibleCategories = useMemo(
    () => categories.filter((c) => getCategoryItems(c.key).length > 0),
    [categories, getCategoryItems]
  );

  // Scroll-spy
  useEffect(() => {
    if (!visibleCategories.length || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScrollRef.current) return;
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const topMost = visible.reduce((best, current) =>
          current.boundingClientRect.top < best.boundingClientRect.top ? current : best
        );
        const key = topMost.target.getAttribute("data-category-key");
        if (key) setActiveCategory(key);
      },
      { rootMargin: "-140px 0px -60% 0px", threshold: 0 }
    );
    document.querySelectorAll("section[data-category-key]").forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [visibleCategories, loading, filteredItems]);

  useEffect(() => {
    if (visibleCategories.length && !activeCategory) {
      setActiveCategory(visibleCategories[0].key);
    }
  }, [visibleCategories, activeCategory]);

  const handleCategoryClick = useCallback((categoryKey) => {
    const section = document.getElementById(`category-${categoryKey}`);
    if (!section) return;
    setActiveCategory(categoryKey);
    isProgrammaticScrollRef.current = true;
    section.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 700);
  }, []);

  // --- Basket handlers (only relevant when mode === weekly_order) ---
  const openQuantityModal = useCallback((item) => {
    setQuantityItem(item);
    setQuantityOpen(true);
  }, []);

  const addToBasket = useCallback(
    (item, quantity, variant = null) => {
      const category = categories.find((c) => c.key === item.category);
      const weekNumber = category?.is_rotating ? selectedWeek?.week_number : null;
      setBasket((prev) => {
        const idx = prev.findIndex(
          (b) => b.item.id === item.id && b.variant?.name === variant?.name
        );
        if (idx >= 0) {
          return prev.map((b, i) =>
            i === idx ? { ...b, quantity: Math.min(MAX_QTY, b.quantity + quantity) } : b
          );
        }
        return [...prev, { item, quantity, variant, weekNumber }];
      });
      toast.success(`${item.name} added to basket`);
      setQuantityOpen(false);
    },
    [categories, selectedWeek]
  );

  const updateBasketQty = useCallback((index, qty) => {
    setBasket((prev) =>
      qty <= 0
        ? prev.filter((_, i) => i !== index)
        : prev.map((b, i) => (i === index ? { ...b, quantity: Math.min(MAX_QTY, qty) } : b))
    );
  }, []);

  const removeFromBasket = useCallback(
    (index) => setBasket((prev) => prev.filter((_, i) => i !== index)),
    []
  );

  const clearBasket = useCallback(() => {
    setBasket([]);
    setBasketOpen(false);
  }, []);

  const basketTotal = useMemo(
    () =>
      basket.reduce((sum, b) => sum + (b.variant ? b.variant.price : b.item.price) * b.quantity, 0),
    [basket]
  );
  const basketCount = useMemo(() => basket.reduce((sum, b) => sum + b.quantity, 0), [basket]);

  // --- Render states ---
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#1aa59a]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm" data-testid="location-not-found">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Location not found</h1>
          <p className="text-gray-500">
            The link you followed may be incorrect or this location is no longer active.
          </p>
        </div>
      </div>
    );
  }

  if (!accessCode) {
    return (
      <LocationAccessGate
        slug={slug}
        locationInfo={locationInfo}
        onVerified={(code) => setAccessCode(code)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <LocationHeader
        location={locationInfo}
        showBasket={isWeeklyOrder}
        basketCount={basketCount}
        basketTotal={basketTotal}
        onBasketClick={() => setBasketOpen(true)}
        showOrdersLink={isWeeklyOrder}
        slug={slug}
      />

      <WeekSelector selectedWeek={selectedWeek} onWeekChange={setSelectedWeek} />

      {locationInfo?.smart_fridge_enabled && !isWeeklyOrder && (
        <SmartFridgeBanner slug={slug} externalUrl={locationInfo.smart_fridge_url} />
      )}

      {visibleCategories.length > 0 && (
        <CategoryFilter
          categories={visibleCategories}
          activeCategory={activeCategory}
          onSelectCategory={handleCategoryClick}
        />
      )}

      <main className="max-w-7xl mx-auto px-4 py-4">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1aa59a]" />
          </div>
        ) : visibleCategories.length === 0 ? (
          <div className="text-center py-16" data-testid="location-empty-menu">
            <p className="text-gray-500 text-lg mb-2">No items available yet</p>
            <p className="text-sm text-gray-400">
              Please contact your office administrator to enable items for {locationInfo?.name}.
            </p>
          </div>
        ) : (
          <>
            {promotions.length > 0 && <PromoGrid promotions={promotions} />}
            {visibleCategories.map((category) => (
              <MenuSection
                key={category.key}
                category={category}
                items={getCategoryItems(category.key)}
                viewOnly={!isWeeklyOrder}
                locationSlug={slug}
                accessCode={accessCode}
                onAddToBasket={isWeeklyOrder ? openQuantityModal : undefined}
              />
            ))}
          </>
        )}
      </main>

      {isWeeklyOrder && (
        <>
          <BasketModal
            open={basketOpen}
            onClose={() => setBasketOpen(false)}
            basket={basket}
            onUpdateQuantity={updateBasketQty}
            onRemove={removeFromBasket}
            onCheckout={() => {
              setBasketOpen(false);
              setCheckoutOpen(true);
            }}
            total={basketTotal}
          />

          <CheckoutModal
            open={checkoutOpen}
            onClose={() => setCheckoutOpen(false)}
            basket={basket}
            total={basketTotal}
            selectedWeek={selectedWeek}
            onSuccess={clearBasket}
            locationSlug={slug}
            customerProfile={locationInfo?.customer_profile}
          />

          <QuantityModal
            open={quantityOpen}
            onClose={() => setQuantityOpen(false)}
            item={quantityItem}
            onConfirm={addToBasket}
          />
        </>
      )}
    </div>
  );
}
